<template>
  <div class="container-fluid">
    <page-header
      :page="header.page"
      :actions="header.actions"
      left-side-class="col-xl-8"
      right-side-class="col-xl-4 align-items-end d-flex pb-2 justify-content-end"
    />
    <filter-manager
      v-model="filter.model"
      :filters="filterOptions"
      class="filter-manager_extended"
      search-container-class="search-btn col-lg-2-custom"
      @center="(val) => getMunicipalities(val?.id)"
      @municipality="(val) => getSchools(val?.id)"
      @search="() => search()"
    />

    <custom-separator
      text="Resultado de las Solicitudes"
      class="my-4 custom-separator-line"
    />

    <div v-if="hasResults" class="search-result">
      <result-row
        v-for="(item, index) in results"
        :key="index"
        :item="item"
        @open="handlerOpen"
        @close="handlerClose"
        @refresh="() => search(pagination.currentPage)"
      />
      <pagination
        v-if="pagination.totalItemCount > pagination.pageSize"
        :data="pagination"
        :page-size="pagination.pageSize"
        @pagination-go-page="search"
      />
    </div>

    <custom-result-not-found
      v-else
      class="mt-4 row pt-3"
      text="No hay información disponible."
    />
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import FilterManager from 'FilterManager';
import Pagination from 'pagination';
import CustomResultNotFound from 'custom-result-not-found';
import CustomSeparator from 'custom-separator';

import storeMixin from '@/mixin/storeGenerator';

import locationService from '@/services/api/location.api';
import materialsAndEquipmentsService from '@/services/api/materialsAndEquipments.api';
import inventoryCatalogService from '@/services/api/inventorycatalog.api';
import inventoryTransferService from '@/services/api/inventoryTransferMaterialsAndEquipments.api';

import ResultRow from './components/ResultRow';

const DEFAULT_PAGE_SIZE = 10;

export default {
  name: 'MaterialsAndEquipmentsTranfer',
  components: {
    PageHeader,
    FilterManager,
    Pagination,
    CustomResultNotFound,
    CustomSeparator,
    ResultRow,
  },
  mixins: [storeMixin],
  data() {
    return {
      results: [],
      filter: {
        model: {
          catalog: null,
          saloonType: null,
          status: null,
          requestCode: null,
          center: null,
          municipality: null,
          school: null,
          requestDate: null,
        },
        options: {
          catalogs: [],
          saloonTypes: [],
          statuses: [],
          centers: [],
          municipalities: [],
          schools: [],
        },
      },
      pagination: {
        count: 0,
        currentPage: 1,
        pageCount: 1,
        totalItemCount: 0,
        pageSize: DEFAULT_PAGE_SIZE,
      },
    };
  },
  async mounted() {
    await Promise.allSettled([
      this.getCatalogs(),
      this.getSaloonTypes(),
      this.getStatuses(),
      this.getCenters(),
    ]);
  },
  computed: {
    academicYearId() {
      return this.currentAcademicYear().id;
    },
    hasResults() {
      return Boolean(this.results.length);
    },
    header() {
      return {
        page: {
          icon: 'fal fa-exchange',
          title: 'Transferencia de Materiales/Equipos',
          routes: ['Inicio', 'PEI', 'Transferencia de Materiales/Equipos'],
        },
        actions: [
          {
            text: 'Regresar',
            icon: 'fas fa-arrow-circle-left',
            variant: 'outline-success',
            click: () => this.$router.back(),
          },
        ],
      };
    },
    filterOptions() {
      return [
        {
          name: 'catalog',
          component: 'CustomDropDown',
          options: this.filter.options.catalogs,
          value: this.filter.model.catalog,
          placeholder: 'Nombre de Material/Equipo',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-sm-6 col-md ml-0 mr-0',
          clearable: true,
        },
        {
          name: 'saloonType',
          component: 'CustomDropDown',
          options: this.filter.options.saloonTypes,
          value: this.filter.model.saloonType,
          placeholder: 'Tipo de Salón',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-sm-6 col-md ml-0 mr-0',
          clearable: true,
        },
        {
          name: 'status',
          component: 'CustomDropDown',
          options: this.filter.options.statuses,
          value: this.filter.model.status,
          placeholder: 'Estatus',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-sm-6 col-md ml-0 mr-0',
          clearable: true,
        },
        {
          name: 'requestCode',
          component: 'CustomInput',
          value: this.filter.model.requestCode,
          placeholder: 'Escriba Número de Solicitud o Código',
          icon: 'fa fa-search orange-icon',
          class: 'col-12 col-sm-6 col-md ml-0 mr-0',
          type: 'text',
          leftIcon: true,
          clearable: true,
        },
        {
          name: 'center',
          component: 'CustomDropDown',
          value: this.filter.model.center,
          options: this.filter.options.centers,
          placeholder: 'Centro',
          class: 'col-12 col-sm-6 col-md ml-0 mr-0',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          isAdvanced: true,
        },
        {
          name: 'municipality',
          component: 'CustomDropDown',
          value: this.filter.model.municipality,
          options: this.filter.options.municipalities,
          placeholder: 'Municipio',
          class: 'col-12 col-sm-6 col-md ml-0 mr-0',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          isAdvanced: true,
          disabled: !this.filter.model.center,
        },
        {
          name: 'school',
          component: 'CustomDropDown',
          value: this.filter.model.school,
          options: this.filter.options.schools,
          placeholder: 'Escuela',
          class: 'col-12 col-sm-6 col-md ml-0 mr-0',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          isAdvanced: true,
          disabled: !this.filter.model.municipality,
        },
        {
          name: 'requestDate',
          component: 'CustomDatePicker',
          placeholder: 'Fecha de Solicitud',
          class: 'col-12 col-sm-6 col-md w-100',
          type: 'date',
          isAdvanced: true,
        },
      ];
    },
  },
  methods: {
    async search(currentPage = 1) {
      const {
        catalog,
        saloonType,
        status,
        requestCode,
        center,
        municipality,
        school,
        requestDate,
      } = this.filter.model;

      const { pageSize } = this.pagination;

      const { data } = await inventoryTransferService.search({
        catalogId: catalog?.id,
        saloonTypeId: saloonType?.id,
        statusId: status?.id,
        centerId: center?.id,
        municipalityId: municipality?.id,
        schoolId: school?.id,
        requestCode,
        requestDate,
        currentPage,
        pageSize,
      });

      this.results = data.data.map((x) => ({
        ...x,
        isOpen: false,
      }));

      this.pagination = {
        pageSize: DEFAULT_PAGE_SIZE,
        totalItemCount: data.totalItemCount,
        pageCount: data.pageCount,
        currentPage: data.currentPage,
        count: data.count,
      };
    },
    async getCatalogs() {
      const { data } = await inventoryCatalogService.getKeyValue({ type: 2 });
      this.filter.options.catalogs = data;
    },
    async getSaloonTypes() {
      const { data } = await materialsAndEquipmentsService.getSaloon();
      this.filter.options.saloonTypes = data;
    },
    async getStatuses() {
      const { data } = await inventoryTransferService.getStatuses();
      this.filter.options.statuses = data;
    },
    async getCenters() {
      const { data } = await locationService.getCenters({
        academicYearId: this.academicYearId,
      });
      this.filter.options.centers = data;

      if (data.length === 1) {
        this.filter.model.center = data[0];
        this.getMunicipalities(data[0].id);
      }
    },
    async getMunicipalities(centerId) {
      this.filter.model.municipality = null;
      this.filter.model.school = null;
      this.filter.options.municipalities = [];
      this.filter.options.schools = [];

      if (!centerId) return;

      const { data } = await locationService.getMunicipalities({
        academicYearId: this.academicYearId,
        centerId,
      });
      this.filter.options.municipalities = data;

      if (data.length === 1) {
        this.filter.model.municipality = data[0];
        this.getSchools(data[0].id);
      }
    },
    async getSchools(municipalityId) {
      this.filter.model.school = null;
      this.filter.options.schools = [];

      if (!municipalityId) return;

      const academicYearId = this.currentAcademicYear().id;
      const { data } = await locationService.getSchools({
        academicYearId,
        centerId: this.filter.model.center.id,
        municipalityId,
      });
      this.filter.options.schools = data;
      if (data.length === 1) this.filter.model.school = data[0];
    },
    async handlerOpen(item) {
      this.results
        .filter((x) => x.id !== item.id && x.isOpen)
        .forEach((x) => (x.isOpen = false));

      item.isOpen = true;
    },
    handlerClose(item) {
      item.isOpen = false;
    },
  },
};
</script>
<style scoped>
::v-deep .custom-datetime-picker-container.width-container {
  width: 100% !important;
}
@media (min-width: 992px) {
  ::v-deep .col-lg-2-custom {
    -webkit-box-flex: 0 !important;
    -ms-flex: 0 0 16.66666667% !important;
    flex: 0 0 16.66666667% !important;
    max-width: 16.66666667% !important;
  }
}
</style>
